import { apiConfig } from './config'

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

type Primitive = string | number | boolean | undefined | null

export type ApiRequestOptions = {
  method?: HttpMethod
  body?: unknown
  headers?: HeadersInit
  params?: Record<string, Primitive | Primitive[]>
  token?: string
  signal?: AbortSignal
}

const buildQueryString = (params?: ApiRequestOptions['params']) => {
  if (!params) return ''
  const searchParams = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return
    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (entry === undefined || entry === null) return
        searchParams.append(key, String(entry))
      })
      return
    }
    searchParams.append(key, String(value))
  })

  const qs = searchParams.toString()
  return qs ? `?${qs}` : ''
}

const resolveUrl = (path: string, params?: ApiRequestOptions['params']) => {
    const isAbsolute = path.startsWith('http');
    const relativePath = path.startsWith('/') ? path : `/${path}`;
    const normalizedPath = isAbsolute ? path : `${apiConfig.baseUrl}${relativePath}`;
    return `${normalizedPath}${buildQueryString(params)}`;
};

export class ApiError extends Error {
  status: number
  data: unknown

  constructor(message: string, status: number, data?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

function buildRequestBody(
    method: string,
    body: ApiRequestOptions['body'],
    headers: Headers,
): BodyInit | undefined {
    if (body instanceof FormData) return body;

    if (body !== undefined && body !== null) {
        headers.set('Content-Type', 'application/json');
        return JSON.stringify(body);
    }

    if (method !== 'GET' && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }

    return undefined;
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
    const { method = 'GET', body, headers, params, token, signal } = options;
    const url = resolveUrl(path, params);
    const requestHeaders = new Headers(headers);

    requestHeaders.set('Accept', 'application/json');
    if (token) {
        requestHeaders.set('Authorization', `Bearer ${token}`);
    }

    const requestBody = buildRequestBody(method, body, requestHeaders);

    const response = await fetch(url, {
        method,
        headers: requestHeaders,
        body: method === 'GET' ? undefined : requestBody,
        signal,
        credentials: 'include',
    });

    if (response.status === 204) {
        if (!response.ok) throw new ApiError(response.statusText, response.status);
        return {} as T;
    }

    const contentType = response.headers.get('Content-Type');
    let payload: unknown;

    if (contentType?.includes('application/json')) {
        try {
            payload = await response.json();
        } catch {
            payload = null;
        }
    } else {
        payload = await response.text();
    }

    if (!response.ok) {
        throw new ApiError(response.statusText || 'Request failed', response.status, payload);
    }

    return payload as T;
}